import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Modal } from 'antd-v5';
import { EditableField } from './EditableField';
import { getRequest } from '../../../../core/apiRequests';
import { updateClient } from '../../../../services/clients';
import { setClientAssociateId } from '../../../../reducers/client';

const Associates = ({
  control,
  isSubmitting,
  saveClientInfo,
  getValues,
  merchantId,
  register,
  handleSubmit,
  merchantConfig,
  setValue,
  auth,
  resetField,
  client,
  watch,
  ...props
}) => {
  const { data } = useQuery(
    'associates-info-drawer',
    async () => {
      const reqObj = {
        params: ['chats', 'getAsctList'],
      };
      const data = await getRequest(reqObj);
      const sortedAscts = data
        ?.sort((a, b) => {
          if (`${a.first_name || ''}${a.last_name || ''}` > `${b.first_name || ''}${b.last_name}`) {
            return 1;
          }
          if (`${a.first_name || ''}${a.last_name || ''}` < `${b.first_name || ''}${b.last_name}`) {
            return -1;
          }
          return 0;
        })
        .map(asct => ({
          value: asct?.id,
          label: `${asct?.first_name || ''} ${asct?.last_name || ''}`,
        }));
      return [{ value: null, label: 'Unassigned' }, ...sortedAscts];
    },
    {
      enabled: !!merchantId,
      refetchOnWindowFocus: false,
    },
  );

  const { data: allAssoc } = useQuery(
    'associates-list-info-drawer',
    async () => {
      const reqObj = {
        params: ['associates'],
        loadingStatusOff: true,
      };
      const response = await getRequest(reqObj);
      const sortedAscts = response
        ?.sort((a, b) => {
          if (`${a.first_name || ''}${a.last_name || ''}` > `${b.first_name || ''}${b.last_name}`) {
            return 1;
          }
          if (`${a.first_name || ''}${a.last_name || ''}` < `${b.first_name || ''}${b.last_name}`) {
            return -1;
          }
          return 0;
        })
        .map(asct => ({
          value: asct?.id,
          label: `${asct?.first_name || ''} ${asct?.last_name || ''}`,
        }));
      return [{ value: null, label: 'Unassigned' }, ...sortedAscts];
    },
    {
      enabled: !!auth.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const getAssociateName = useCallback(() => {
    if (data) {
      const associate = data.find(asct => asct.value === getValues('clientObj.associate_id'));
      return associate?.label;
    }
    return null;
  }, [data, getValues('clientObj.associate_id'), watch('clientObj.associate_id')]);

  const getCustomButton = useMemo(() => {
    if (auth.role !== 'ASSOCIATE') {
      return null;
    }
    if (auth.userId === getValues('clientObj.associate_id')) {
      return 'Unassign Me';
    }
    if (!getValues('clientObj.associate_id')) {
      return 'Assign Me';
    }
  }, [auth.userId, getValues('clientObj.associate_id')]);

  const customButtonClosedAction = useCallback(async () => {
    if (auth.role !== 'ASSOCIATE') {
      return;
    }
    const currentAssociateId = getValues('clientObj.associate_id');

    if (auth.userId === currentAssociateId) {
      // "Unassign Me" — show confirmation modal
      const confirmed = await new Promise(resolve => {
        Modal.confirm({
          title: 'Unassign Associate',
          content:
            'Unassigning yourself will also unassign any of your open reminders for this client. Open sales opportunities will remain assigned to you. Do you want to continue?',
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!confirmed) return;

      setValue('clientObj.associate_id', null, { shouldDirty: false });
      // Unassigning also clears the client's open reminders for this associate
      // (cascade handled by clientbook-api).
      await updateClient({
        data: {
          associateId: 0,
        },
        loadingStatusOff: true,
        clientId: client.client_id,
      });
      setClientAssociateId(null);
      return;
    }
    if (!currentAssociateId) {
      // "Assign Me" — no previous associate, no confirmation needed
      setValue('clientObj.associate_id', auth.userId, { shouldDirty: false });
      setClientAssociateId(auth.userId);
      await updateClient({
        data: {
          associateId: auth.userId,
        },
        loadingStatusOff: true,
        clientId: client.client_id,
      });
    }
  }, [auth.userId, getValues('clientObj.associate_id'), setValue, client.client_id]);

  return (
    <EditableField
      label="Assigned Associate"
      value={getAssociateName() || 'Unassigned'}
      valueClassName={!getValues('clientObj.associate_id') ? 'color-bdbdbd' : ''}
      containerClassName="p-top-8 m-btm-8"
      type="select"
      control={control}
      register={register('clientObj.associate_id')}
      reset={() => resetField('clientObj.associate_id')}
      onSave={handleSubmit}
      selectProps={{
        options: allAssoc,
      }}
      isSubmitting={isSubmitting}
      customButtonClosed={getCustomButton}
      customButtonClosedAction={customButtonClosedAction}
      editable={
        auth.role !== 'ASSOCIATE' ||
        auth.userId === getValues('clientObj.associate_id') ||
        getValues('clientObj.associate_id') === null
      }
    />
  );
};

const mapStateToProps = state => ({
  client: state.client,
});
export default connect(mapStateToProps)(Associates);
